import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { VaStatementOfTruth } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  fullNameReducer,
  statementOfTruthFullName,
} from '~/platform/forms/components/review/PreSubmitSection';
import { setPreSubmit as setPreSubmitAction } from 'platform/forms-system/src/js/actions';
import { querySelectorWithShadowRoot } from 'platform/utilities/ui/webComponents';

import { getTitle } from '../helpers';

function PreSubmitInfo({
  formData,
  showError,
  setPreSubmit,
  onSectionComplete,
}) {
  // local state
  const [signatureBlurred, setSignatureBlurred] = useState(false);
  const [inputError, setInputError] = useState(null);
  const [checkboxError, setCheckboxError] = useState(null);
  const [sectionComplete, setSectionComplete] = useState(false);
  const expectedFullName = statementOfTruthFullName(
    formData,
    {
      fullNamePath: 'certifyingOfficial',
    },
    null,
  );
  const title = getTitle(formData?.certifyingOfficial?.role);

  // event handlers
  const handleCheckboxChange = useCallback(
    event => {
      setPreSubmit('statementOfTruthCertified', event.detail.checked);
    },
    [setPreSubmit],
  );

  const handleInputChange = useCallback(
    event => {
      setPreSubmit('statementOfTruthSignature', event.detail.value);
    },
    [setPreSubmit],
  );

  const handleInputBlur = useCallback(
    () => setSignatureBlurred(true),
    [setSignatureBlurred],
  );

  const removeOldPrivacyPolicy = async () => {
    const privacyPolicyText = await querySelectorWithShadowRoot(
      'p.short-line',
      document.querySelector('va-statement-of-truth'),
    );
    privacyPolicyText?.setAttribute('style', 'display:none;');
  };

  // effects: need to update formData and errors based on what the user
  // has typed and if they've checked the checkbox
  useEffect(() => {
    // check for name-matching error
    const namesMatch =
      fullNameReducer(formData.statementOfTruthSignature) ===
      fullNameReducer(expectedFullName);
    setInputError(
      (showError || signatureBlurred) && !namesMatch
        ? `Enter your full name exactly as entered on the form: ${expectedFullName}`
        : null,
    );

    // check for checkbox error
    setCheckboxError(
      showError && !formData.statementOfTruthCertified
        ? 'You must certify by checking the box'
        : null,
    );

    const isComplete = namesMatch && formData.statementOfTruthCertified;
    if (sectionComplete !== isComplete) {
      setSectionComplete(isComplete);
      onSectionComplete(isComplete);
    }

    const initializeComponent = async () => {
      // Hide platform line for privacy policy, use custom
      await removeOldPrivacyPolicy();
    };

    initializeComponent();
  }, [
    formData.statementOfTruthSignature,
    formData.statementOfTruthCertified,
    showError,
    signatureBlurred,
    onSectionComplete,
    expectedFullName,
    sectionComplete,
  ]);

  return (
    <div className="vads-u-margin-top--4">
      <VaStatementOfTruth
        heading="Certification statement"
        inputLabel="Your full name"
        checkboxLabel="I certify that the information above is correct and true to the best of my knowledge and belief."
        inputValue={formData.statementOfTruthSignature}
        checked={formData.statementOfTruthCertified}
        inputMessageAriaDescribedby="I have read and accept the privacy policy."
        onVaInputChange={handleInputChange}
        onVaInputBlur={handleInputBlur}
        onVaCheckboxChange={handleCheckboxChange}
        inputError={inputError}
        checkboxError={checkboxError}
        hideLegalNote
      >
        <div>
          <p>
            I certify that my entries are true and correct to the best of my
            knowledge. I agree to immediately notify the VA of any potential
            violations of the above prohibitions. I further acknowledge that
            this certification must be completed and signed by a certifying
            official, owner, officer, or another individual in an official
            capacity, such as the President or Chief Administrative Officer of
            the institution.
          </p>
          <span data-testid="privacy-policy-text">
            I have read and accept the{' '}
            <va-link
              text="privacy policy"
              aria-label="View the privacy policy"
              href="/privacy-policy"
              external
            />
            .
          </span>
          <p className="vads-u-margin-bottom--0">Your title</p>
          <strong>
            <p className="vads-u-font-size--h3 vads-u-margin-top--0">{title}</p>
          </strong>
        </div>
      </VaStatementOfTruth>
    </div>
  );
}

PreSubmitInfo.propTypes = {
  formData: PropTypes.object.isRequired,
  preSubmitInfo: PropTypes.shape({
    statementOfTruth: PropTypes.shape({
      heading: PropTypes.string,
      textInputLabel: PropTypes.string,
      messageAriaDescribedby: PropTypes.string,
      body: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
      fullNamePath: PropTypes.string,
    }).isRequired,
  }).isRequired,
  setPreSubmit: PropTypes.func.isRequired,
  onSectionComplete: PropTypes.func.isRequired,
  showError: PropTypes.bool,
};

const mapDispatchToProps = {
  setPreSubmit: setPreSubmitAction,
};

export { PreSubmitInfo as BasePresubmitInfo };

export default connect(null, mapDispatchToProps)(PreSubmitInfo);
